// ═══════════════════════════════════════════════════════════════
// dialecteApi.js — Service Le Dialecte (S54 P1)
// Client de /api/studio/images/optimize-prompt (livré S53)
// ═══════════════════════════════════════════════════════════════

const getToken = () => localStorage.getItem('oracle_token');

export class DialecteError extends Error {
    constructor(status, code, message, retryAfter) {
        super(message || 'Erreur Dialecte');
        this.name = 'DialecteError';
        this.status = status;
        this.code = code;
        this.retryAfter = retryAfter;
    }
}

/**
 * Traduit un prompt brut dans le dialecte du modèle image cible.
 * Timeout client 20s (> 15s backend) pour laisser une marge explicite.
 *
 * @param {string} model     — model_id cible (ex. "google/gemini-2.5-flash-image")
 * @param {string} rawPrompt — prompt brut utilisateur (3-2000 chars)
 * @param {Object} [opts]
 * @param {boolean} [opts.deep=false]      — true = Sonnet (analyse fine), false = Haiku (rapide)
 * @param {number|null} [opts.projectId]   — id projet pour tracking usage (optionnel)
 * @returns {Promise<Object>} { optimized_prompt, rationale, model_used, deep, cost_usd, duration_ms, chunks_used, base_mode, ... }
 * @throws {DialecteError} en cas d'erreur HTTP (status, code, retryAfter mappés)
 */
export async function translate(model, rawPrompt, { deep = false, projectId = null } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 20000);
    try {
        const res = await fetch('/api/studio/images/optimize-prompt', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`,
            },
            body: JSON.stringify({
                model,
                raw_prompt: rawPrompt,
                deep,
                project_id: projectId,
            }),
            signal: ctl.signal,
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new DialecteError(
                res.status,
                body.code || 'unknown',
                body.message,
                body.retry_after,
            );
        }
        return body;
    } finally {
        clearTimeout(timer);
    }
}
